// import packages
import React, { useState, useContext, useCallback, useEffect } from "react";
import _ from "lodash";
import {
  Button,
  Card,
  Collapsible,
  Caption,
  Form,
  FormLayout,
  Layout,
  PageActions,
  SettingToggle,
  TextContainer,
  TextStyle,
} from "@shopify/polaris";
import { ActionButton } from "app/setup/modules/operator/features/productTagSetting/style";

// import context
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

import { useMutation, useQuery } from "@apollo/react-hooks";

// import propTypes
import { advanceProp } from "app/setup/modules/operator/features/commission/props";

// import components
import { Banner, Spinner, Toast } from "lib/components";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import queries
import { GET_METAFIELD_SETTING } from "app/product/apollo/queries";
import { UPDATE_METAFIELD } from "app/productOld/apollo/mutations";

import ProductMetafieldAdd from "app/productOld/modules/operator/features/metafield/components/add";

const ProductMetafield = () => {
  const { cms = {}, history } = useContext(PrivateContext);

  const [disableNameKey, setDisableNameKey] = useState([]);
  const [state, setState] = useState([]);

  // const [active, setActive] = useState(false);
  // const [collapsOpen, setCollapseOpen] = useState(false);
  const [submitEnabled, setSubmitEnable] = useState(false);

  const [banner, setBanner] = useState({ isOpen: false, status: "", title: "", message: "" });
  const [message, setMessage] = useState("");
  const [updateAt, setUpdateAt] = useState("");

  const { loading: catalogLoading, data: metaFieldData, error: getProductTagSettingError, refetch } = useQuery(
    GET_METAFIELD_SETTING
  );

  const [updateMetafieldSetting, { loading: updateTagSettingLoading }] = useMutation(UPDATE_METAFIELD);

  useEffect(() => {
    if (metaFieldData) {
      const metafieldSetting = baseHelper.getResponseData(metaFieldData, constant.gql.GET_METAFIELD_SETTING) || {};
      const { metafield = [], updatedAt } = metafieldSetting;
      setUpdateAt(updatedAt);
      if (metafield && metafield.length) {
        setState(metafield);
      }
    }
  }, [constant.gql.GET_METAFIELD_SETTING, metaFieldData]);

  // load product catalog data

  const updateVal = (option, val, item) => {
    setSubmitEnable(true);
    setBanner({ isOpen: false, status: "", title: "" });
    const advancedValue = [...state];
    if (option === constant.NAME) {
      advancedValue[item].name = null;
    }
    if (option === constant.VALUE) {
      advancedValue[item].value = null;
    }
    if (option === constant.CONTENT_TYPE) {
      advancedValue[item].value = null;
      advancedValue[item].contentType = val;
    } else {
      advancedValue[item][option] = val;
    }
    setState(advancedValue);
  };

  const uniqueVal = (option, val, item) => {
    const advancedValue = [...state];
    if (option === constant.NAMESPACE || option === constant.KEY) {
      advancedValue.forEach((advanceData, index) => {
        if ((advanceData.namespace === val || advanceData.key === val) && item !== index) {
          const uniquekey = advanceData.key === val ? (advancedValue[item].key = null) : "";
          // eslint-disable-next-line no-unused-expressions
          advanceData.namespace === val ? (advancedValue[item].namespace = null) : uniquekey;
          setBanner({ isOpen: true, status: constant.CRITICAL, title: cms("message.error.unique") });
        }
        return null;
      });
    }
    setState(advancedValue);
  };

  const add = () => {
    const { length } = state;
    if (length > 1) {
      const line = state[length - 1];
      const keys = Object.keys(line);
      let isInvalid = false;
      keys.forEach((key) => {
        if (!line[key]) {
          isInvalid = true;
        }
      });
      if (isInvalid) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: cms("message.error.row") });
        return;
      }
    }

    const row = {
      contentType: null,
      key: null,
      name: null,
      value: null,
      namespace: null,
    };
    setDisableNameKey([...disableNameKey, state.length]);
    state.push(row);
    setState([...state]);
    setSubmitEnable(true);
  };

  const removeItem = (item) => {
    setSubmitEnable(true);
    state.splice(item, 1);
    const disableFields = [...disableNameKey];
    disableFields.forEach((key, index) => {
      disableFields[index] = key - 1;
    });
    setDisableNameKey(disableFields);
    setState([...state]);
  };

  const dismissBanner = () => setBanner({ isOpen: false, status: "", title: "", message: "" });

  const validateValue = () => {
    let isInvalid = false;
    let isKeyInvalid = false;
    let isNamespaceInvalid = false;
    state.forEach((line) => {
      const keys = Object.keys(line);
      keys.forEach((key) => {
        if (!line[key]) {
          isInvalid = true;
        }
        if (key === constant.NAMESPACE && !isInvalid && line[key].length < 3) {
          isNamespaceInvalid = true;
        }
        if (key === constant.KEY && !isInvalid && line[key].length < 3) {
          isKeyInvalid = true;
        }
      });
      if (isInvalid) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: cms("message.error.row") });
      }
      if (!isInvalid && isKeyInvalid && isNamespaceInvalid) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: cms("message.error.both"),
        });
      } else {
        if (!isInvalid && isKeyInvalid) {
          setBanner({ isOpen: true, status: constant.CRITICAL, title: cms("message.error.key") });
        }
        if (!isInvalid && isNamespaceInvalid) {
          setBanner({
            isOpen: true,
            status: constant.CRITICAL,
            title: cms("message.error.namespace"),
          });
        }
      }
    });
    return isInvalid || isKeyInvalid || isNamespaceInvalid;
  };

  // const handleToggle = useCallback(() => {
  //   setActive(!active);
  //   setCollapseOpen(!collapsOpen);
  // });

  const handleSubmit = async () => {
    try {
      if (!validateValue()) {
        const advancedValue = [...state];
        advancedValue.forEach((advanceData, index) => {
          if (advanceData.contentType === constant.MEASUREMENT && advanceData && advanceData.value) {
            advancedValue[index].value =
              advanceData && _.isArray(advanceData.value) ? advanceData.value : [advanceData.value];
          }
        });
        setState(advancedValue);
        setSubmitEnable(false);
        setDisableNameKey([]);

        const res = await updateMetafieldSetting({
          variables: {
            input: { metafield: state },
          },
        });
        const resData = baseHelper.getResponseData(res.data, constant.gql.UPDATE_METAFIELD);
        const resError = baseHelper.getResponseError(res.data, constant.gql.UPDATE_METAFIELD);
        if (resError) {
          setBanner({
            status: constant.CRITICAL,
            isOpen: true,
            title: resError,
          });
        }
        if (resData) {
          setMessage(cms("message.success.metafield"));
          setTimeout(() => {
            // history.push("/products");
          }, 1500);
          refetch();
        }
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  // const contentStatus = active ? constant.displayStatus.DISABLE : constant.ENABLE;

  if (catalogLoading) {
    return <Spinner />;
  }

  return (
    <Layout.AnnotatedSection title={cms("title")} description={cms("description")}>
      <Card
        title={[
          cms("cardTitle"),
          <TextStyle variation="subdued">
            {updateAt && <Caption>{`${baseHelper.lastUpdateDate(updateAt)}`}</Caption>}
          </TextStyle>,
        ]}
      >
        <Card.Section>
          <TextContainer>{cms("cardDescription")}</TextContainer>
          {/* <div className="toggle-action">
            <SettingToggle
              action={{
                content: contentStatus,
                onAction: handleToggle,
                destructive: active,
              }}
              enabled={active}
            >
              <TextStyle variation="strong">{cms("title")}</TextStyle>
              <div className="card-discription">
                <TextContainer>{cms("cardDescription")}</TextContainer>
              </div>
            </SettingToggle>
          </div>
          <br /> */}
          <br />
          <Collapsible
            open
            id="advancedCommission"
            transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
            expandOnPrint
          >
            {banner.isOpen && (
              <>
                <Banner
                  isOpen={banner.isOpen}
                  status={banner.status}
                  title={banner.title}
                  isScrollTop={false}
                  onDismiss={() => dismissBanner()}
                >
                  {banner.message}
                </Banner>
                <br />
              </>
            )}
            <Form>
              <FormLayout>
                <ProductMetafieldAdd
                  advancedValue={state}
                  removeItem={removeItem}
                  updateVal={updateVal}
                  count={state.length}
                  uniqueVal={uniqueVal}
                  disableNameKey={disableNameKey}
                />
              </FormLayout>
              <Button plain id="addLink" onClick={add}>
                {`+${cms("label.metafield")}`}
              </Button>
              <br />
              <ActionButton>
                {updateAt && (
                  <PageActions
                    primaryAction={{
                      content: cms("common.button.update"),
                      onAction: () => handleSubmit(),
                      disabled: !submitEnabled,
                      loading: updateTagSettingLoading,
                    }}
                    secondaryActions={[
                      {
                        content: cms("common.button.cancel"),
                        onAction: () => history.push("/setting"),
                      },
                    ]}
                  />
                )}
              </ActionButton>
            </Form>
          </Collapsible>
        </Card.Section>
      </Card>
      {!updateAt && (
        <PageActions
          primaryAction={{
            content: cms("common.button.submit"),
            onAction: () => handleSubmit(),
            disabled: !submitEnabled,
            loading: updateTagSettingLoading,
          }}
          secondaryActions={[
            {
              content: cms("common.button.cancel"),
              onAction: () => history.push("/setting"),
            },
          ]}
        />
      )}
      <div className="toast">
        <Toast message={message} setToast={setMessage} />
      </div>
    </Layout.AnnotatedSection>
  );
};

ProductMetafield.propTypes = advanceProp.type;

export default withFeature(withErrorBoundary(ProductMetafield), { feature: constant.PRODUCT_METAFIELD_SETTING });
